import "./App.scss";
import "./assets/css/Navigation.scss";
import { ThemeProvider } from "@mui/material/styles";
import { ErrorProvider, useError } from "./providers/ErrorProvider";
import { HttpInterceptorService } from "./services/HttpInterceptorService";
import { useEffect, useMemo, Suspense } from "react";
import type { ReactNode } from "react";
import { LicenseInfo } from "@mui/x-license";
import { VITE_MUI_X_PRO_KEY } from "./utilities/env";
import { ErrorType } from "./types/errors";
import { Box } from "@mui/material";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { router } from "./Router";
import { claTheme } from "lib-seamlesscomponents-react";
import { ToastContainer } from "react-toastify";

LicenseInfo.setLicenseKey(VITE_MUI_X_PRO_KEY || "");

export interface Navbar {
  label: string;
  path: string;
}

const ErrorHandlingSetup = ({ children }: { children: ReactNode }) => {
  const { addError } = useError();

  useEffect(() => {
    HttpInterceptorService.setupInterceptors(undefined, (processedError) => {
      addError(processedError);
    });

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      addError({
        message: "An unexpected error occurred. Please try again.",
        type: "APPLICATION_ERROR" as ErrorType,
        originalError: event.reason,
      });
    };

    const handleError = (event: ErrorEvent) => {
      console.error("Global error:", event.error);
      addError({
        message: "An unexpected error occurred. Please try again.",
        type: "APPLICATION_ERROR" as ErrorType,
        originalError: event.error,
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
      window.removeEventListener("error", handleError);
    };
  }, [addError]);

  return <>{children}</>;
};

export const App = () => {
  const appRouter = useMemo(() => createBrowserRouter(router), []);

  return (
    <ErrorProvider>
      <ThemeProvider theme={claTheme}>
        <ErrorHandlingSetup>
        <ToastContainer
              position={'top-right'}
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
          />
          <Suspense
            fallback={
              <Box textAlign="center" p={10}>
                Loading...
              </Box>
            }
          >
            <RouterProvider router={appRouter} />
          </Suspense>
        </ErrorHandlingSetup>
      </ThemeProvider>
    </ErrorProvider>
  );
};

export default App;
